import { Form, FormLabel, FormControl, FormItem, FormField, FormMessage } from "@/shared/ui/form.tsx";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover.tsx";
import { Select, SelectItem, SelectGroup, SelectLabel, SelectValue, SelectContent, SelectTrigger } from "@/shared/ui/select.tsx";
import { Calendar } from "@/shared/ui/calendar.tsx";
import { Button } from "@/shared/ui/button.tsx";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { Input } from "@/shared/ui/input.tsx";
import { format } from "date-fns";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod"
import { SetStateAction, Dispatch, useEffect } from "react";
import { PlaygroundResponseType, useGetResults } from "../api/play-api";
import { Textarea } from "@/shared/ui/textarea.tsx";
import ProgressLoader from "./ProgressLoader";

const EventFormSchema = z.object({
  eventName: z.string(),
  description: z.string(),
  eventType: z.enum([
    "National Holidays",
    "Global Holidays",
    "Cultural and Religious Festivals",
    "Seasonal Promotions",
    "Awareness Days",
    "Professional Appreciation Days",
    "Health and Wellness Days",
    "Cause Awareness Days",
    "Anniversaries",
    "Local Events",
    "Social Responsibility Initiatives",
    "Environmental Initiatives",
    "Famous Personalities birthdays",
    "Sports Events",
    "Financial Milestones",
    "Healthcare Innovations",
    "Entertainment Releases"
  ]),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  frequency: z.enum(["Yearly", "Monthly", "Weekly"]),
  activity: z.enum(["Post", "Campaign"])
})
export type EventFormType = z.infer<typeof EventFormSchema>;

type EventFormProps = {
  setResult: Dispatch<SetStateAction<PlaygroundResponseType | undefined>>;
  setEventId: React.Dispatch<SetStateAction<number>>
}

const EventForm = ({ setResult, setEventId }: EventFormProps) => {

  const { results, getResults, status } = useGetResults()

  const form = useForm<EventFormType>({
    resolver: zodResolver(EventFormSchema),
  })

  useEffect(() => {
    if (results) {
      if (results.data !== null) {
        setResult(results)
        setEventId(results.data.event_id)
      }
    }
  }, [results, setResult])

  const submitHandler = (formData: EventFormType) => {
    console.log(formData)
    getResults(formData)
  }

  return (
    <div className="w-full flex justify-center">
      <Form {...form}>
        <form className="w-full sm:w-1/2 max-sm:px-4 grid grid-cols-2 gap-2" onSubmit={form.handleSubmit(submitHandler)}>
          <FormField
            control={form.control}
            name="eventName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-bold">Event Name</FormLabel>
                <FormControl>
                  <Input {...field} className="bg-white" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="eventType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-bold">Event Type</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <SelectTrigger className=" flex-grow ">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Select Event Type</SelectLabel>
                        <SelectItem value="National Holidays">National Holidays</SelectItem>
                        <SelectItem value="Global Holidays">Global Holidays</SelectItem>
                        <SelectItem value="Cultural and Religious Festivals">Cultural and Religious Festivals</SelectItem>
                        <SelectItem value="Seasonal Promotions">Seasonal Promotions</SelectItem>
                        <SelectItem value="Awareness Days">Awareness Days</SelectItem>
                        <SelectItem value="Professional Appreciation Days">Professional Appreciation Days</SelectItem>
                        <SelectItem value="Health and Wellness Days">Health and Wellness Days</SelectItem>
                        <SelectItem value="Cause Awareness Days">Cause Awareness Days</SelectItem>
                        <SelectItem value="Anniversaries">Anniversaries</SelectItem>
                        <SelectItem value="Local Events">Local Events</SelectItem>
                        <SelectItem value="Social Responsibility Initiatives">Social Responsibility Initiatives</SelectItem>
                        <SelectItem value="Environmental Initiatives">Environmental Initiatives</SelectItem>
                        <SelectItem value="Famous Personalities birthdays">Famous Personalities birthdays</SelectItem>
                        <SelectItem value="Sports Events">Sports Events</SelectItem>
                        <SelectItem value="Financial Milestones">Financial Milestones</SelectItem>
                        <SelectItem value="Healthcare Innovations">Healthcare Innovations</SelectItem>
                        <SelectItem value="Entertainment Releases">Entertainment Releases</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="font-bold">Description</FormLabel>
                <FormControl>
                  <Textarea rows={2} {...field} className="bg-white" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <span className="flex flex-col mt-2">
                  <FormLabel className=" mb-2 font-bold">Start Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "pl-3 text-left font-normal",
                            !field.value && "text-slate-600",
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-80" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </span>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <span className="flex flex-col mt-2">
                  <FormLabel className=" mb-2 font-bold">End Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "pl-3 text-left font-normal",
                            !field.value && "text-slate-600",
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-80" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </span>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-bold">Frequency</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <SelectTrigger className=" flex-grow ">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Select</SelectLabel>
                        <SelectItem value="Yearly">Yearly</SelectItem>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Weekly">Weekly</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="activity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-bold">Activity</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <SelectTrigger className=" flex-grow ">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Select</SelectLabel>
                        <SelectItem value="Post">Post</SelectItem>
                        <SelectItem value="Campaign">Campaign</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="w-full col-span-2 flex justify-end py-3">
            {status === "idle" && <Button>Submit</Button>}
            {status === "pending" && <ProgressLoader />}
          </div>
        </form>
      </Form>
    </div>
  )
}

export default EventForm
